import React, { FC } from 'react';
import { BillingInterval, BillingPlanCard, BillingPlanKey } from '../../../models/billing.models';
import { formatMoney, isAcademicEmail } from '../../../helpers/billing.helpers';
import './plan-picker.component.css';

export interface PlanPickerProps {
  plans: BillingPlanCard[];
  selectedKey: BillingPlanKey | null;
  /** Billing interval chosen on each card. Academic stays yearly. */
  intervals: Partial<Record<BillingPlanKey, BillingInterval>>;
  /** Shown under each price, e.g. "6 month free trial". Null hides it. */
  trialLabel: string | null;
  /** Set when this account already has an Academic team. */
  academicUnavailableReason?: string | null;
  /** Small and Large stay on this cycle. Academic is always yearly. */
  lockedInterval?: BillingInterval | null;
  onSelect: (key: BillingPlanKey) => void;
  onIntervalChange: (key: BillingPlanKey, interval: BillingInterval) => void;
}

const PLAN_ACCENT: Record<string, string> = {
  small_team: '#93B8FF',
  academic: '#1D4ED8',
};

const planDotCount = (key: BillingPlanKey): number => {
  if (key === 'small_team') return 1;
  if (key === 'program') return 3;
  return 5;
};

/** "One team · up to 10 members (coaches and athletes)" -> ["One team · up to 10 members", "coaches and athletes"] */
const splitCapacity = (capacity: string): [string, string | null] => {
  const match = capacity.match(/^(.*?)\s*\(([^)]+)\)\s*$/);
  return match ? [match[1], match[2]] : [capacity, null];
};

const CheckIcon: FC = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M5 12.5l4.5 4.5L19 7" />
  </svg>
);

const PlanPicker: FC<PlanPickerProps> = ({
  plans,
  selectedKey,
  intervals,
  trialLabel,
  academicUnavailableReason = null,
  lockedInterval = null,
  onSelect,
  onIntervalChange,
}) => (
  <div className="plan-picker">
    {plans.map((plan) => {
      const hasMonth = plan.allowedIntervals.includes('month');
      const hasYear = plan.allowedIntervals.includes('year');
      const interval: BillingInterval =
        plan.key !== 'academic' && lockedInterval
          ? lockedInterval
          : hasMonth && hasYear
          ? intervals[plan.key] || 'month'
          : hasYear
          ? 'year'
          : 'month';
      const intervalLocked = plan.key !== 'academic' && Boolean(lockedInterval);
      const priceAmount = interval === 'year' ? plan.yearlyPrice || 0 : plan.monthlyPrice || 0;
      const priceSuffix = interval === 'year' ? '/ year' : '/ month';
      const [capacity, ...featureBullets] = plan.bullets;
      const [capacityMain, capacityNote] = splitCapacity(capacity || '');
      const needsEdu = plan.key === 'academic' && !isAcademicEmail();
      const academicTaken = plan.key === 'academic' && Boolean(academicUnavailableReason);
      const academicLocked = needsEdu || academicTaken;
      const academicTitle = needsEdu
        ? 'Academic requires an email address that ends with .edu.'
        : academicUnavailableReason || undefined;
      const isSelected = selectedKey === plan.key && !academicLocked;

      return (
        <button
          key={plan.key}
          type="button"
          className={`plan-card${isSelected ? ' plan-card-selected' : ''}${plan.featured ? ' plan-card-featured' : ''}${
            academicLocked ? ' plan-card-locked' : ''
          }`}
          style={{ '--plan-accent': PLAN_ACCENT[plan.key] || '#93B8FF' } as React.CSSProperties}
          onClick={() => {
            if (academicLocked) return;
            onSelect(plan.key);
          }}
          disabled={academicLocked}
          aria-pressed={isSelected}
          title={academicLocked ? academicTitle : undefined}
        >
          <span className="plan-card-check">
            <CheckIcon />
          </span>
          {plan.featured ? <span className="plan-card-tag">Most chosen</span> : null}

          <span className="plan-card-icon" aria-hidden>
            {Array.from({ length: planDotCount(plan.key) }).map((_, index) => (
              <i key={index} />
            ))}
          </span>

          <span className="plan-card-name">{plan.name}</span>
          <span className="plan-card-eyebrow">
            {needsEdu ? 'Requires a .edu email' : academicTaken ? 'Contact CoeLab for another' : plan.eyebrow}
          </span>

          <span className="plan-card-price">
            <b>{formatMoney(priceAmount)}</b>
            <s>{priceSuffix}</s>
          </span>

          {trialLabel ? <span className="plan-card-trial">{trialLabel}</span> : null}

          {capacity ? (
            <span className="plan-card-capacity">
              {capacityMain}
              {capacityNote ? <em>({capacityNote})</em> : null}
            </span>
          ) : null}

          <hr />
          <ul className="plan-card-bullets">
            {featureBullets.map((bullet) => (
              <li key={bullet}>{bullet}</li>
            ))}
          </ul>

          {hasMonth && hasYear && !intervalLocked ? (
            <span
              className="plan-card-toggle"
              onClick={(event) => event.stopPropagation()}
              role="group"
              aria-label={`${plan.name} billing interval`}
            >
              <button
                type="button"
                className={interval === 'month' ? 'active' : ''}
                onClick={() => {
                  onIntervalChange(plan.key, 'month');
                  onSelect(plan.key);
                }}
              >
                Monthly
              </button>
              <button
                type="button"
                className={interval === 'year' ? 'active' : ''}
                onClick={() => {
                  onIntervalChange(plan.key, 'year');
                  onSelect(plan.key);
                }}
              >
                Yearly
              </button>
            </span>
          ) : null}
        </button>
      );
    })}
  </div>
);

export default PlanPicker;
